import { Button, ButtonProps, CircularProgress } from '@mui/material';
import { useState, ReactNode } from 'react';
import ModalConfirmation from '../../layout/ModalConfirmation';

type SttButtonProps = {
    caption?: string;
    icon?: ReactNode;
    variant?: ButtonProps['variant'];
    disabled?: boolean;
    loading?: boolean | null;
    confirmation?: boolean;
    onClick?: () => void;
    children?: ReactNode;
} & Omit<ButtonProps, 'loading'>;

/**
 * General button with loading state and confirmation dialog support
 */
export function SttButton({
    caption,
    icon,
    variant = 'outlined',
    disabled = false,
    loading = false,
    confirmation = false,
    onClick,
    children,
    fullWidth,
    ...rest
}: SttButtonProps) {
    const [showConfirmation, setShowConfirmation] = useState(false);

    const isDisabled = !!(loading || disabled);

    const handleClick = () => {
        if (confirmation) {
            setShowConfirmation(true);
        } else {
            onClick?.();
        }
    };

    const handleConfirm = () => {
        onClick?.();
        setShowConfirmation(false);
    };

    const handleCancel = () => {
        setShowConfirmation(false);
    };

    return (
        <span className={`relative inline-flex${fullWidth ? ' w-full' : ''}`}>
            <Button
                variant={variant}
                disabled={isDisabled}
                startIcon={icon}
                fullWidth={fullWidth}
                onClick={handleClick}
                {...rest}
            >
                {children ??
                    (caption !== undefined ? <span>{caption}</span> : null)}
            </Button>

            {loading && (
                <CircularProgress
                    size={24}
                    className="absolute top-1/2 left-1/2 -mt-3 -ml-3"
                />
            )}

            <ModalConfirmation
                show={showConfirmation}
                onClose={handleCancel}
                onAccept={handleConfirm}
            />
        </span>
    );
}
